import { EmptyState } from '@atlas/ui-components'
import { Input } from '@atlas/ui-primitives'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { AxisValueDTO } from '@/services/classification.service'
import { ValueRow, type ValueRowActions } from './ValueRow'

/**
 * Flat-axis value list with a live search filter — for long lists (e.g. Brand's
 * 196). When `actions` are provided each row is editable (label/status) +
 * retireable (per-axis manager); without them it's read-only links (overview).
 * Renders as a responsive grid of self-contained cells (actions wrap INSIDE the
 * cell — they can't overflow the card).
 */
export function FlatValueList({
	values,
	onSave,
	onRetire,
	onMerge,
	saving,
}: { values: AxisValueDTO[] } & Partial<ValueRowActions>) {
	const [q, setQ] = useState('')
	const filtered = useMemo(() => {
		const needle = q.trim().toLowerCase()
		if (!needle) return values
		return values.filter((v) => v.label.toLowerCase().includes(needle) || v.code.toLowerCase().includes(needle))
	}, [values, q])

	return (
		<div className="space-y-3">
			<div className="flex items-center gap-3">
				<Input className="max-w-xs" placeholder="Filter values…" value={q} onChange={(e) => setQ(e.target.value)} />
				<span className="text-sm text-ds-text-muted">
					{filtered.length}
					{filtered.length !== values.length ? ` / ${values.length}` : ''} values
				</span>
			</div>
			{filtered.length === 0 ? (
				<EmptyState title="No matches." variant="filtered" />
			) : (
				<div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(15rem,1fr))]">
					{filtered.map((v) => (
						<div key={v.valueId} className="rounded-ds-base border border-ds-border px-3 py-2 overflow-hidden">
							{onSave && onRetire ? (
								<ValueRow value={v} onSave={onSave} onRetire={onRetire} onMerge={onMerge} saving={saving} />
							) : (
								<div className="text-sm truncate">
									<Link className="hover:underline" to={`/admin/classification/values/${v.valueId}/mappings`}>
										{v.label}
									</Link>
									<Link
										className="text-ds-primary hover:underline"
										to={`/admin/classification/values/${v.valueId}/availability`}
									>
										availability
									</Link>
									<span className="ml-1 text-xs text-ds-text-subtle">{v.code}</span>
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	)
}
